import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

// ─── 类型定义 ──────────────────────────────────────────────────

export interface ModalityItem {
  name: string;
  nameEn: string;
  icon: string;
  color: string;
  colorLight: string;
  sample: string;
}

export interface OutputItem {
  label: string;
  icon: string;
}

export interface MultimodalAIFusionProps {
  /** 主标题 */
  title?: string;
  /** 副标题 */
  subtitle?: string;
  /** 三种模态数据 */
  modalities?: ModalityItem[];
  /** 融合核心标签文字 */
  fusionLabel?: string;
  /** 融合核心图标 */
  fusionIcon?: string;
  /** 输出能力列表 */
  outputItems?: OutputItem[];
  /** 总结标题 */
  summaryTitle?: string;
  /** 总结副标题 */
  summarySubtitle?: string;
  /** 背景渐变 */
  background?: string;
  /** 标题渐变色 */
  titleGradient?: string;
  /** 模态卡片之间的水平间距 */
  modalSpacing?: number;
  /** 模态卡片顶部 Y 坐标 */
  modalTopY?: number;
  /** 编码器标签 Y 坐标 */
  encoderY?: number;
  /** 融合核心相对画布中心的 Y 偏移 */
  fusionOffsetY?: number;
  /** 输出区相对融合核心的 Y 偏移 */
  outputOffsetY?: number;
  /** 输出卡片之间的水平间距 */
  outputSpacing?: number;
}

// ─── 默认值 ──────────────────────────────────────────────────

const defaultModalities: ModalityItem[] = [
  {
    name: '文本',
    nameEn: 'Text',
    icon: '📝',
    color: '#5B8DEF',
    colorLight: 'rgba(91,141,239,0.15)',
    sample: '"今天天气真好"',
  },
  {
    name: '图像',
    nameEn: 'Image',
    icon: '🖼️',
    color: '#A855F7',
    colorLight: 'rgba(168,85,247,0.15)',
    sample: '1024 × 768 px',
  },
  {
    name: '音频',
    nameEn: 'Audio',
    icon: '🎵',
    color: '#F43F5E',
    colorLight: 'rgba(244,63,94,0.15)',
    sample: '16kHz / 5.2s',
  },
];

const defaultOutputItems: OutputItem[] = [
  { label: '跨模态搜索', icon: '🔍' },
  { label: '内容生成', icon: '✨' },
  { label: '场景理解', icon: '🧠' },
];

/**
 * 多模态 AI 融合可视化
 *
 * 叙事线（150帧 / 5秒 @30fps）：
 *   Phase 1 (0-25)   : 标题 + 副标题引入概念
 *   Phase 2 (15-55)  : 三种模态（文本 / 图像 / 音频）依次登场，附带示例数据
 *   Phase 3 (45-85)  : 各模态通过专用编码器转为向量
 *   Phase 4 (70-110) : 编码向量汇聚至中央融合核心
 *   Phase 5 (100-135): 融合后展示跨模态能力
 *   Phase 6 (125-150): 总结文案淡入
 */
export const MultimodalAIFusion: React.FC<MultimodalAIFusionProps> = ({
  title = '多模态 AI 融合',
  subtitle = '将文本、图像、音频统一到同一个理解空间',
  modalities = defaultModalities,
  fusionLabel = '统一语义空间',
  fusionIcon = '🔮',
  outputItems = defaultOutputItems,
  summaryTitle = '一个模型，理解所有模态',
  summarySubtitle = 'Unified Model · Universal Understanding',
  background = 'linear-gradient(160deg, #0F172A 0%, #1E1B4B 50%, #0F172A 100%)',
  titleGradient = 'linear-gradient(135deg, #818CF8, #C084FC, #F472B6)',
  modalSpacing = 420,
  modalTopY: _modalTopY = 330,
  encoderY: _encoderY = 520,
  fusionOffsetY = 210,
  outputOffsetY = 130,
  outputSpacing = 340,
}) => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  const cx = width / 2;
  const cy = height / 2;

  // ─── helpers ──────────────────────────────────────────────────
  const clampedInterpolate = (
    f: number,
    inputRange: [number, number],
    outputRange: [number, number],
  ) =>
    interpolate(f, inputRange, outputRange, {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    });

  const sp = (delay: number) =>
    spring({ frame: frame - delay, fps, config: { damping: 14, mass: 0.8 } });

  // ─── 布局参数 ──────────────────────────────────────────────
  const modalX = (i: number) => cx + (i - 1) * modalSpacing;
  const modalTopY = _modalTopY;
  const encoderY = _encoderY;
  const fusionCoreY = cy + fusionOffsetY;
  const outputY = fusionCoreY + outputOffsetY;

  const modalityDelays = [15, 25, 35];

  // ─── Phase 1: 标题 ──────────────────────────────────────────
  const titleOpacity = clampedInterpolate(frame, [0, 20], [0, 1]);
  const titleY = clampedInterpolate(frame, [0, 20], [-40, 0]);
  const subtitleOpacity = clampedInterpolate(frame, [8, 28], [0, 1]);

  // ─── Phase 3: 编码器 ──────────────────────────────────────
  const encoderOpacity = clampedInterpolate(frame, [45, 60], [0, 1]);

  // ─── Phase 4: 融合核心 ──────────────────────────────────────
  const fusionScale = sp(75);
  const fusionPulse = 1 + Math.sin(frame * 0.12) * 0.06;

  const particles = Array.from({ length: 45 }, (_, i) => {
    const mi = i % 3;
    const offset = Math.floor(i / 3) * 4;
    const startFrame = 70 + offset;
    const t = clampedInterpolate(frame, [startFrame, startFrame + 30], [0, 1]);

    const startX = modalX(mi);
    const startY = encoderY + 30;
    const endX = cx;
    const endY = fusionCoreY;

    const wobble = Math.sin(i * 1.7) * 30 * (1 - t);

    return {
      x: interpolate(t, [0, 1], [startX, endX]) + wobble,
      y:
        interpolate(t, [0, 1], [startY, endY]) +
        Math.sin(i * 2.3) * 15 * (1 - t),
      opacity: interpolate(t, [0, 0.3, 0.8, 1], [0, 0.9, 0.7, 0]),
      size: interpolate(t, [0, 1], [7, 3]),
      color: modalities[mi]?.color ?? '#888',
    };
  });

  // ─── Phase 5: 输出能力 ──────────────────────────────────────
  const outputOpacity = clampedInterpolate(frame, [105, 120], [0, 1]);

  // ─── Phase 6: 总结 ──────────────────────────────────────────
  const endOpacity = clampedInterpolate(frame, [125, 140], [0, 1]);

  // ─── 连接线绘制进度 ───────────────────────────────────────
  const connectionDraw = clampedInterpolate(frame, [55, 80], [0, 1]);
  const outputLineDraw = clampedInterpolate(frame, [105, 120], [0, 1]);

  // ─── 背景星点（装饰） ────────────────────────────────────
  const stars = Array.from({ length: 30 }, (_, i) => ({
    x: (i * 347 + 123) % width,
    y: (i * 571 + 89) % height,
    size: 1.5 + (i % 3),
    opacity: 0.15 + Math.sin(frame * 0.04 + i * 0.8) * 0.1,
  }));

  return (
    <AbsoluteFill
      style={{
        background,
        fontFamily:
          '"SF Pro Display", "PingFang SC", "Noto Sans SC", -apple-system, sans-serif',
        overflow: 'hidden',
      }}
    >
      {/* ── 背景装饰 ─────────────────────────────────────── */}
      {stars.map((s, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: s.x,
            top: s.y,
            width: s.size,
            height: s.size,
            borderRadius: '50%',
            background: '#fff',
            opacity: s.opacity,
          }}
        />
      ))}

      {/* ── 微妙光晕 ─────────────────────────────────────── */}
      <div
        style={{
          position: 'absolute',
          left: cx - 400,
          top: cy - 300,
          width: 800,
          height: 600,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 70%)',
          filter: 'blur(40px)',
        }}
      />

      {/* ── Phase 1: 标题 ──────────────────────────────────── */}
      <div
        style={{
          position: 'absolute',
          top: 50,
          width: '100%',
          textAlign: 'center',
          opacity: titleOpacity,
          transform: `translateY(${titleY}px)`,
        }}
      >
        <div
          style={{
            fontSize: 70,
            fontWeight: 800,
            background: titleGradient,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: -1,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 28,
            color: 'rgba(255,255,255,0.5)',
            marginTop: 8,
            opacity: subtitleOpacity,
            letterSpacing: 4,
          }}
        >
          {subtitle}
        </div>
      </div>

      {/* ── SVG 连接线层 ──────────────────────────────────── */}
      <svg
        width={width}
        height={height}
        style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
      >
        <defs>
          {modalities.map((m, i) => (
            <linearGradient
              key={`grad-${i}`}
              id={`line-grad-${i}`}
              x1="0%"
              y1="0%"
              x2="0%"
              y2="100%"
            >
              <stop offset="0%" stopColor={m.color} stopOpacity="0.6" />
              <stop offset="100%" stopColor={m.color} stopOpacity="0.1" />
            </linearGradient>
          ))}
        </defs>

        {/* 模态 → 编码器 → 融合核心 连接线 */}
        {modalities.map((m, i) => {
          const mx = modalX(i);
          const lineEndY = interpolate(
            connectionDraw,
            [0, 1],
            [encoderY, fusionCoreY],
          );
          return (
            <g key={`conn-${i}`} opacity={connectionDraw}>
              <line
                x1={mx}
                y1={modalTopY + 70}
                x2={interpolate(connectionDraw, [0, 0.5, 1], [mx, mx, cx])}
                y2={lineEndY}
                stroke={`url(#line-grad-${i})`}
                strokeWidth="2.5"
                strokeDasharray="6,4"
              />
            </g>
          );
        })}

        {/* 融合核心 → 输出 连接线 */}
        {outputItems.map((_, i) => {
          const targetX = cx + (i - 1) * outputSpacing;
          return (
            <line
              key={`out-line-${i}`}
              x1={cx}
              y1={fusionCoreY + 40}
              x2={interpolate(outputLineDraw, [0, 1], [cx, targetX])}
              y2={interpolate(
                outputLineDraw,
                [0, 1],
                [fusionCoreY + 40, outputY],
              )}
              stroke="rgba(255,255,255,0.15)"
              strokeWidth="2"
              opacity={outputLineDraw}
            />
          );
        })}
      </svg>

      {/* ── Phase 2: 模态卡片 ──────────────────────────────── */}
      {modalities.map((m, i) => {
        const scale = sp(modalityDelays[i] ?? 15 + i * 10);
        const mx = modalX(i);

        return (
          <div
            key={m.nameEn}
            style={{
              position: 'absolute',
              left: mx - 150,
              top: modalTopY - 55,
              width: 300,
              height: 150,
              borderRadius: 20,
              background: m.colorLight,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${m.color}30`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              transform: `scale(${scale})`,
              opacity: scale,
            }}
          >
            <div style={{ fontSize: 44 }}>{m.icon}</div>
            <div
              style={{
                fontSize: 26,
                fontWeight: 700,
                color: '#fff',
                marginTop: 6,
              }}
            >
              {m.name}
              <span
                style={{
                  color: 'rgba(255,255,255,0.4)',
                  marginLeft: 8,
                  fontSize: 22,
                }}
              >
                {m.nameEn}
              </span>
            </div>
            <div
              style={{
                fontSize: 22,
                color: 'rgba(255,255,255,0.45)',
                marginTop: 4,
              }}
            >
              {m.sample}
            </div>
          </div>
        );
      })}

      {/* ── Phase 3: 编码器标签 ────────────────────────────── */}
      {modalities.map((m, i) => {
        const mx = modalX(i);
        return (
          <div
            key={`enc-${i}`}
            style={{
              position: 'absolute',
              left: mx - 80,
              top: encoderY - 18,
              width: 200,
              height: 40,
              borderRadius: 8,
              background: `${m.color}25`,
              border: `1px solid ${m.color}40`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 22,
              fontWeight: 600,
              color: m.color,
              opacity: encoderOpacity,
              transform: `scale(${interpolate(encoderOpacity, [0, 1], [0.8, 1])})`,
              letterSpacing: 1,
            }}
          >
            {m.nameEn} Encoder
          </div>
        );
      })}

      {/* ── Phase 4: 融合粒子 ──────────────────────────────── */}
      {particles.map((p, i) => (
        <div
          key={`p-${i}`}
          style={{
            position: 'absolute',
            left: p.x - p.size / 2,
            top: p.y - p.size / 2,
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            background: p.color,
            boxShadow: `0 0 ${p.size * 2}px ${p.color}`,
            opacity: p.opacity,
          }}
        />
      ))}

      {/* ── Phase 4: 融合核心 ──────────────────────────────── */}
      <div
        style={{
          position: 'absolute',
          left: cx - 55,
          top: fusionCoreY - 55,
          width: 110,
          height: 110,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(255,255,255,0.95) 0%, rgba(168,85,247,0.4) 60%, transparent 80%)',
          boxShadow:
            '0 0 50px rgba(168,85,247,0.5), 0 0 100px rgba(99,102,241,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transform: `scale(${fusionScale * fusionPulse})`,
          opacity: fusionScale,
        }}
      >
        <div style={{ fontSize: 42 }}>{fusionIcon}</div>
      </div>

      {/* 融合核心标签 */}
      <div
        style={{
          position: 'absolute',
          left: cx,
          top: fusionCoreY + 62,
          transform: 'translateX(-50%)',
          fontSize: 22,
          fontWeight: 600,
          color: 'rgba(255,255,255,0.7)',
          opacity: fusionScale,
          letterSpacing: 2,
        }}
      >
        {fusionLabel}
      </div>

      {/* ── Phase 5: 输出能力 ──────────────────────────────── */}
      {outputItems.map((item, i) => {
        const scale = sp(105 + i * 6);
        const targetX = cx + (i - 1) * outputSpacing;

        return (
          <div
            key={`out-${i}`}
            style={{
              position: 'absolute',
              left: targetX - 120,
              top: outputY - 30,
              width: 240,
              height: 60,
              borderRadius: 14,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              transform: `scale(${scale})`,
              opacity: outputOpacity * scale,
            }}
          >
            <span style={{ fontSize: 26 }}>{item.icon}</span>
            <span style={{ fontSize: 24, fontWeight: 600, color: '#fff' }}>
              {item.label}
            </span>
          </div>
        );
      })}

      {/* ── Phase 6: 总结 ──────────────────────────────────── */}
      <div
        style={{
          position: 'absolute',
          bottom: 18,
          width: '100%',
          textAlign: 'center',
          opacity: endOpacity,
        }}
      >
        <div
          style={{
            fontSize: 30,
            fontWeight: 700,
            background: 'linear-gradient(90deg, #818CF8, #F472B6)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {summaryTitle}
        </div>
        <div
          style={{
            fontSize: 24,
            color: 'rgba(255,255,255,0.35)',
            marginTop: 6,
          }}
        >
          {summarySubtitle}
        </div>
      </div>
    </AbsoluteFill>
  );
};
